import { canv } from "@/canv/global";
import { DEFAULT_TEXT_COLOR, LINK_COLOR, STRIKE_COLOR } from "../constantsV2";
import type { TextLine, TextV2Data } from "../typesV2";
import {
    calculateLineWidth,
    calculateStrikeMetrics,
    calculateUnderlineMetrics,
    getBaseFont,
    getBulletListMarkerSlotWidth,
    getLineStartX,
    getListBulletWidth,
    getListNumberWidth,
    getListNumberX,
    getOrderedListMarkerSlotWidth,
    getSegmentFont,
    getSegmentWidth,
    getTextStartX,
    setCanvasFont,
} from "./measurements";

export const drawLinePlaceholder = (
    line: TextLine,
    lineY: number,
    data: TextV2Data,
    startX: number,
    horizontalPadding: number,
    verticalPadding: number,
    lineHeight: number,
    lineWidth: number,
): void => {
    if (line.segments.length === 0 && !line.listType) {
        return;
    }
    const lineStartX = getLineStartX(lineWidth, startX, data.textAlign);
    const rectY = lineY - lineHeight + verticalPadding;
    const rectHeight = lineHeight - 2 * verticalPadding;

    canv.ctx.fillRect(lineStartX, rectY, lineWidth, rectHeight);
};

export const drawListBullet = (
    lineStartX: number,
    textY: number,
    color: string,
    data: TextV2Data,
    horizontalPadding: number,
): void => {
    const bulletText = "•";
    const slotStartX = getListNumberX(lineStartX, horizontalPadding);
    const bulletWidth = getListBulletWidth(data);
    const slotWidth = getBulletListMarkerSlotWidth(data);
    const bulletX = slotStartX + Math.max(0, slotWidth - bulletWidth);

    canv.ctx.save();
    setCanvasFont(getBaseFont(data));
    canv.ctx.fillStyle = color;
    canv.ctx.fillText(bulletText, bulletX, textY);
    canv.ctx.restore();
};

export const drawListNumber = (
    line: TextLine,
    lineStartX: number,
    y: number,
    color: string,
    data: TextV2Data,
    horizontalPadding: number,
): void => {
    if ((line.listNumber ?? 0) <= 0) {
        return;
    }

    const numberText = `${line.listNumber ?? 1}.`;
    const slotStartX = getListNumberX(lineStartX, horizontalPadding);
    const numberWidth = getListNumberWidth(line.listNumber ?? 1, data);
    const slotWidth = getOrderedListMarkerSlotWidth(data);
    const numberX = slotStartX + Math.max(0, slotWidth - numberWidth);

    canv.ctx.save();
    setCanvasFont(getBaseFont(data));
    canv.ctx.fillStyle = color;
    canv.ctx.fillText(numberText, numberX, y);
    canv.ctx.restore();
};

export const drawStrikeLine = (startX: number, y: number, width: number, height: number): void => {
    canv.ctx.save();
    canv.ctx.fillStyle = STRIKE_COLOR;
    canv.ctx.fillRect(startX, y, width, height);
    canv.ctx.restore();
};

export const drawUnderlineLine = (
    startX: number,
    y: number,
    width: number,
    height: number,
    color: string,
) => {
    canv.ctx.save();
    canv.ctx.fillStyle = color;
    canv.ctx.fillRect(startX, y, width, height);
    canv.ctx.restore();
};

export const drawUnderline = (
    line: TextLine,
    lineStartX: number,
    lineY: number,
    data: TextV2Data,
    horizontalPadding: number,
    lineHeight: number,
) => {
    const { underlineHeight, underlineY } = calculateUnderlineMetrics(lineHeight, lineY);

    let underlineStartX: number | null = null;
    let underlineWidth = 0;
    let underlineColor: string | null = null;

    let currentX = getTextStartX(line, lineStartX, data, horizontalPadding);

    for (const segment of line.segments) {
        const segmentWidth = getSegmentWidth(segment, data);

        const shouldUnderline = !!segment.underline || !!segment.link;
        const currentUnderlineColor = segment.link ? LINK_COLOR : STRIKE_COLOR;

        if (shouldUnderline) {
            if (underlineStartX == null) {
                underlineStartX = currentX;
                underlineWidth = segmentWidth;
                underlineColor = currentUnderlineColor;
            } else if (underlineColor === currentUnderlineColor) {
                underlineWidth += segmentWidth;
            } else {
                if (!underlineColor) {
                    return;
                }

                drawUnderlineLine(
                    underlineStartX,
                    underlineY,
                    underlineWidth,
                    underlineHeight,
                    underlineColor,
                );

                underlineStartX = currentX;
                underlineWidth = segmentWidth;
                underlineColor = currentUnderlineColor;
            }
        } else {
            if (underlineStartX !== null && underlineWidth > 0 && underlineColor) {
                drawUnderlineLine(
                    underlineStartX,
                    underlineY,
                    underlineWidth,
                    underlineHeight,
                    underlineColor,
                );
            }

            underlineStartX = null;
            underlineWidth = 0;
            underlineColor = null;
        }

        currentX += segmentWidth;
    }

    if (underlineStartX !== null && underlineWidth > 0 && underlineColor) {
        drawUnderlineLine(
            underlineStartX,
            underlineY,
            underlineWidth,
            underlineHeight,
            underlineColor,
        );
    }
};

/**
 * Рисует линии зачеркивания для зачеркнутых сегментов текста.
 * Объединяет последовательные зачеркнутые сегменты в одну линию.
 */
export const drawStrike = (
    line: TextLine,
    lineStartX: number,
    lineY: number,
    data: TextV2Data,
    horizontalPadding: number,
    lineHeight: number,
): void => {
    const { strikeHeight, strikeY } = calculateStrikeMetrics(lineHeight, lineY);

    let strikeStartX: number | null = null;
    let strikeWidth = 0;
    let currentX = getTextStartX(line, lineStartX, data, horizontalPadding);

    for (const segment of line.segments) {
        const segmentWidth = getSegmentWidth(segment, data);

        if (segment.strike) {
            if (strikeStartX === null) {
                strikeStartX = currentX;
            }
            strikeWidth += segmentWidth;
        } else {
            if (strikeStartX !== null && strikeWidth > 0) {
                drawStrikeLine(strikeStartX, strikeY, strikeWidth, strikeHeight);
            }
            strikeStartX = null;
            strikeWidth = 0;
        }

        currentX += segmentWidth;
    }

    if (strikeStartX !== null && strikeWidth > 0) {
        drawStrikeLine(strikeStartX, strikeY, strikeWidth, strikeHeight);
    }
};

export const drawListMarker = (
    line: TextLine,
    lineStartX: number,
    textY: number,
    data: TextV2Data,
    horizontalPadding: number,
): void => {
    const firstSegmentColor = line.segments[0]?.color || DEFAULT_TEXT_COLOR;

    if (line.listType === "bullet") {
        drawListBullet(lineStartX, textY, firstSegmentColor, data, horizontalPadding);
    } else if (line.listType === "ordered") {
        drawListNumber(line, lineStartX, textY, firstSegmentColor, data, horizontalPadding);
    }
};

export const drawTextSegments = (
    line: TextLine,
    lineStartX: number,
    textY: number,
    data: TextV2Data,
    horizontalPadding: number,
): void => {
    let currentX = getTextStartX(line, lineStartX, data, horizontalPadding);
    let remainingSegments = line.segments.length;

    for (const segment of line.segments) {
        const font = getSegmentFont(segment, data);
        setCanvasFont(font);
        canv.ctx.fillStyle = segment.link ? LINK_COLOR : segment.color;
        canv.ctx.fillText(segment.text, currentX, textY);

        remainingSegments -= 1;
        if (remainingSegments > 0) {
            currentX += getSegmentWidth(segment, data);
        }
    }
};

export const drawLine = (
    line: TextLine,
    lineY: number,
    data: TextV2Data,
    startX: number,
    horizontalPadding: number,
    verticalPadding: number,
    lineHeight: number,
    precomputedLineWidth?: number,
): void => {
    const lineWidth = precomputedLineWidth ?? calculateLineWidth(line, data, horizontalPadding);
    const lineStartX = getLineStartX(lineWidth, startX, data.textAlign);
    const textY = lineY - verticalPadding;
    let hasStrike = false;
    let hasUnderline = false;

    for (const segment of line.segments) {
        hasStrike ||= !!segment.strike;
        hasUnderline ||= !!segment.underline || !!segment.link;

        if (hasStrike && hasUnderline) {
            break;
        }
    }

    drawListMarker(line, lineStartX, textY, data, horizontalPadding);
    drawTextSegments(line, lineStartX, textY, data, horizontalPadding);
    if (hasStrike) {
        drawStrike(line, lineStartX, lineY, data, horizontalPadding, lineHeight);
    }
    if (hasUnderline) {
        drawUnderline(line, lineStartX, textY, data, horizontalPadding, lineHeight);
    }
};
